import React from 'react';
import { motion } from 'framer-motion';
import zappyImg from '../assets/zappy/zappy.png';
import birthdayImg from '../assets/zappy/birthday_offer.png';
import bulkImg from '../assets/zappy/bulk_campaign.png';
import chatbotImg from '../assets/zappy/chatbot_flow.png';

const Zappy = () => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto px-6 py-16"
    >
      <img src={zappyImg} alt="Zappy Logo" className='w-full h-20 object-contain mb-4'></img>
      <h1 className="text-4xl font-bold text-center text-blue-700 mb-10">
        Your Ultimate WhatsApp Marketing Tool
      </h1>

      <p className="text-center text-lg text-gray-600 max-w-3xl mx-auto mb-14">
        Zappy helps you turn conversations into conversions. Whether it's personalized campaigns, auto-replies, or 24/7 chatbot support — we simplify communication so you can focus on growing your business.
      </p>

      {/* Visual Use Cases */}
      <div className="grid md:grid-cols-3 gap-8 mb-20">
        <div className="bg-white rounded-xl shadow-xl p-6 text-center">
          <img src={bulkImg} alt="Bulk Messaging" className="w-full h-40 object-contain mb-4" />
          <h3 className="text-xl font-bold text-blue-700 mb-2">Send 1000s of Messages in One Click</h3>
          <p className="text-sm text-gray-600">
            Broadcast bulk offers to your customers with 90%+ open rates. Stay compliant and avoid number bans with Zappy’s smart routing.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 text-center">
          <img src={birthdayImg} alt="Personalized Birthday Campaign" className="w-full h-40 object-contain mb-4" />
          <h3 className="text-xl font-bold text-blue-700 mb-2">Birthday & Anniversary Automation</h3>
          <p className="text-sm text-gray-600">
            Send personalized greetings like "🎉 15% Off Just for You, Happy Birthday!" to re-engage customers and boost loyalty.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 text-center">
          <img src={chatbotImg} alt="Chatbot Flow" className="w-full h-40 object-contain mb-4" />
          <h3 className="text-xl font-bold text-blue-700 mb-2">24×7 Smart Chatbot</h3>
          <p className="text-sm text-gray-600">
            Build no-code chatbot flows to handle bookings, queries, and lead collection — even while you're asleep.
          </p>
        </div>
      </div>

      {/* Why Use Zappy – Left-aligned Icons */}
      <div className="mb-20 px-4">
        <h2 className="text-3xl font-extrabold text-center text-blue-700 mb-4">
          Why People Love Zappy 💙
        </h2>
        <p className="text-center text-gray-500 max-w-xl mx-auto mb-12 text-sm">
          Zappy brings automation, personalization, and reliability — all in one WhatsApp toolkit.
        </p>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Feature Blocks */}
          {[{
            icon: '🛡️',
            title: '100% Safe Messaging',
            desc: 'Stay compliant and avoid bans with approved WhatsApp API routing.',
          }, {
            icon: '📊',
            title: 'Track Interactions',
            desc: 'View click-throughs, replies, and user activity on every message.',
          }, {
            icon: '🔗',
            title: 'Powerful Integrations',
            desc: 'Connect seamlessly with Google Sheets, CRMs, and custom workflows.',
          }, {
            icon: '⏰',
            title: 'Smart Reminders',
            desc: 'Automatically nudge leads with payment, booking, or renewal reminders.',
          }, {
            icon: '⚡',
            title: 'Quick Setup',
            desc: 'Go live in under 10 minutes — no developer or tech team needed.',
          }, {
            icon: '💰',
            title: 'Affordable Support',
            desc: 'Personal onboarding & full support included with every plan.',
          }].map(({ icon, title, desc }, idx) => (
            <div key={idx} className="flex items-start gap-4">
              <div className="text-3xl bg-blue-100 text-blue-700 rounded-full w-14 h-14 flex items-center justify-center shadow-md">
                {icon}
              </div>
              <div>
                <h3 className="text-lg font-bold text-blue-700">{title}</h3>
                <p className="text-sm text-gray-600">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-50 rounded-2xl p-10 text-center shadow-xl">
        <h3 className="text-2xl font-bold text-blue-700 mb-3">
          Ready to simplify your customer communication?
        </h3>
        <p className="text-gray-600 mb-6">
          Get started with Zappy today and see how effortless marketing can be.
        </p>
        <a
          href="/pricing"
          className="inline-block bg-blue-700 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-blue-800"
        >
          View Pricing Plans
        </a>
      </div>
    </motion.section>
  );
};

export default Zappy;
